import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { BaseLayoutComponent } from './layout/base-layout/base-layout.component';
import { PagePermssions, routePath, USERTYPE } from './core/services/utils/constants';
import { AuthGuard } from './core/guard/auth.guard';
import { UnderDevelopmentComponent } from './modules/under-development/under-development.component';
import { GuestGuard } from './core/guard/guest.guard';
import { UserProfileComponent } from './modules/user-management/user-profile/user-profile.component';
import { ViewApplyActivityComponent } from './modules/self-registration/view-apply-activity/view-apply-activity.component';
import { AddBankComponent } from './modules/self-registration/add-bank/add-bank.component';
import { ApplicantDetailsComponent } from './modules/self-registration/applicant-details/applicant-details.component';
import { CoreCourseViewdetailsComponent } from './modules/self-registration/core-course-viewdetails/core-course-viewdetails.component';
import { BusinessCompetitionDetailsComponent } from './modules/self-registration/business-competition-details/business-competition-details.component';
import { PlacementApprenticeshipDetailsComponent } from './modules/self-registration/empowerhub-page/empowerhub/placement-apprenticeship-details/placement-apprenticeship-details.component';
import { CustomPreviewDetailTableComponent } from './shared/components/custom-table/custom-preview-detail-table/custom-preview-detail-table.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '',
    pathMatch: 'full'
  },
  {
    path: '',
    canActivate: [GuestGuard],
    loadChildren: () => import('./modules/self-registration/self-registration.module').then(m => m.SelfRegistrationModule),
  },
  {
    path: 'auth',
    title: 'Login',
    canActivate: [GuestGuard],
    loadChildren: () => import('./modules/auth/auth.module').then(m => m.AuthModule),
  },
  {
    path: 'portal/auth',
    title: 'Login',
    canActivate: [GuestGuard],
    loadChildren: () => import('./modules/auth/auth.module').then(m => m.AuthModule),
  },
  // Backstop redirects for legacy login URLs Google still has indexed
  // (e.g. /portal/login from an older app version where the login route
  // lived at that path). nginx.prod.conf returns a server-side 301 in
  // production so the crawler updates its index; these Angular routes
  // catch anything that bypasses nginx — in-app links, future bugs, or
  // local dev where the SPA is served directly without the prod nginx
  // config. Without them the user lands on the wildcard → access-denied.
  {
    path: 'portal/login',
    redirectTo: '/portal/auth/login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    redirectTo: '/portal/auth/login',
    pathMatch: 'full',
  },
  {
    path: 'portal/preview/detail',
    component: CustomPreviewDetailTableComponent
  },
  {
    path: 'portal',
    component: BaseLayoutComponent,
    children: [
      {
        path: '',
        redirectTo: '/portal/auth',
        pathMatch: 'full'
      },
      {
        path: 'home',
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
        loadChildren: () => import('./modules/self-registration/home/home/home.module').then(m => m.HomeModule),
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Home', page_title: 'home', requiredPermission: [PagePermssions.VIEW_BENEFICIARY_PROFILE] },

      },
      {
        path: 'applicant-details/:pageType',
        component: ApplicantDetailsComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard]
      },
      {
        path: 'applicant-details',
        component: ApplicantDetailsComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard]
      },
      {
        path: ':pageType/applicant-details',
        component: ApplicantDetailsComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard]
      },
      {
        path: 'add-bank/:pageType',
        component: AddBankComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
      },
      {
        path: 'add-bank',
        component: AddBankComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
      },
      {
        path: 'core-course/detailspage/:id',
        component: CoreCourseViewdetailsComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
      },
      {
        path: 'business-competition/detailspage/:id',
        component: BusinessCompetitionDetailsComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
      },
      {
        path: 'placement/detailspage/:id',
        component: PlacementApprenticeshipDetailsComponent,
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
      },
      {
        path: 'apply-activity/:pagename/:id',
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
        component: ViewApplyActivityComponent
      },
      {
        path: 'user-management',
        title: 'User Management',
        data: {
          id: USERTYPE.ADMIN_ID, breadcrumb: 'User Management', page_title: 'test page title', requiredPermission: [
            PagePermssions.VIEW_CUSTOMUSER,
            PagePermssions.VIEW_CUSTOMGROUP,
          ]
        },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/user-management/user-management.module').then(m => m.UserManagementModule),
        canLoad: [AuthGuard],
      },
      {
        path: 'user-profile',
        canActivate: [AuthGuard],
        data: {
          page_title: 'User', requiredPermission: []
        },
        component: UserProfileComponent
      },
      {
        path: 'masters',
        title: 'Masters',
        data: {
          id: USERTYPE.ADMIN_ID, breadcrumb: 'Masters', page_title: 'test page title', requiredPermission: [
            PagePermssions.VIEW_CATEGORY,
            PagePermssions.VIEW_WORKFLOW,
            PagePermssions.VIEW_BUSINESS,
            PagePermssions.VIEW_DEMOGRAPHICS,
            PagePermssions.VIEW_TRAINING,
            PagePermssions.VIEW_FIRM,
            PagePermssions.VIEW_COURSE_MASTER,
            PagePermssions.VIEW_HOST_INSTITUTION_MASTER,
            PagePermssions.VIEW_BUSINESS_COMPETITION_MASTER,
            PagePermssions.VIEW_CUPF_MASTER,
            PagePermssions.VIEW_GRIEVANCE_MASTER,
            PagePermssions.VIEW_DASHBOARD_MASTER,
            PagePermssions.VIEW_GBV_MASTER,
          ]
        },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/masters/masters.module').then(m => m.MastersModule),
        canLoad: [AuthGuard],
      },
      {
        path: 'workflow',
        title: 'Workflow Management',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Workflow Management', page_title: 'test page title', requiredPermission: [PagePermssions.VIEW_WORKFLOW] },
        loadChildren: () => import('./modules/workflow-management/workflow-management.module').then(m => m.WorkflowManagementModule),
      },
      {
        path: 'firm',
        title: 'Firm Management',
        data: {
          id: USERTYPE.ADMIN_ID, breadcrumb: 'Firm Management', page_title: 'test page title', requiredPermission: [
            PagePermssions.VIEW_FIRMS
          ]
        },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/firm-management/firm-management.module').then(m => m.FirmManagementModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'configuration',
        title: 'Configuration',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Configuration', page_title: 'test page title', requiredPermission: [
            PagePermssions.VIEW_CATEGORY,
            PagePermssions.VIEW_WORKFLOW,
            PagePermssions.VIEW_BUSINESS,
            PagePermssions.VIEW_DEMOGRAPHICS,
            PagePermssions.VIEW_TRAINING,
            PagePermssions.VIEW_FIRM,
            PagePermssions.VIEW_COURSE_MASTER,
            PagePermssions.VIEW_HOST_INSTITUTION_MASTER,
            PagePermssions.VIEW_BUSINESS_COMPETITION_MASTER,
            PagePermssions.VIEW_CUPF_MASTER,
            PagePermssions.VIEW_GRIEVANCE_MASTER,
            PagePermssions.VIEW_DASHBOARD_MASTER,
            PagePermssions.VIEW_GBV_MASTER,
        ] },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/configuration/configuration.module').then(m => m.ConfigurationModule),
        canLoad: [AuthGuard],
      },
      {
        path: 'activities',
        title: 'Activities',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Activities', page_title: 'test page title', requiredPermission: [PagePermssions.VIEW_ACTIVITY] },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/activity/activity.module').then(m => m.ActivityModule),
        canLoad: [AuthGuard],
      },
      {
        path: 'wep',
        title: 'WEP',
        data: {
          id: USERTYPE.ADMIN_ID, breadcrumb: 'WEP', page_title: 'test page title', requiredPermission: [
            PagePermssions.VIEW_WEP
          ]
        },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/wep/wep.module').then(m => m.WepModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'summary',
        title: 'IPF Summary',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'IPF Summary', page_title: 'IPF Summary', requiredPermission: [PagePermssions.VIEW_COMPONENTS, PagePermssions.VIEW_SUB_COMPONENTS] },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/ipf-summary/ipf-summary.module').then(m => m.IPFSummaryModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'courses',
        title: 'Core Courses',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Core Courses', page_title: 'Core Courses', requiredPermission: [PagePermssions.VIEW_CORE_COURSE, PagePermssions.VIEW_CORE_COURSE_APPLICANTS] },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/core-courses/core-courses.module').then(m => m.CoreCoursesModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'beneficiary',
        title: 'Beneficiary',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Beneficiary', page_title: 'Beneficiary', requiredPermission: [PagePermssions.VIEW_BENEFICIARY] },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/beneficiaries/beneficiaries.module').then(m => m.BeneficiariesModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'empowerhub',
        title: 'GROW Services',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'GROW Services', page_title: 'GROW Services', requiredPermission: [PagePermssions.VIEW_ENPOWERHUB] },
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/self-registration/empowerhub-page/empowerhub/empowerhub.module').then(m => m.EmpowerhubModule),
        canLoad: [AuthGuard],
      },
      {
        path: 'host-institution',
        title: 'Host Institutions',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Host Institutions', page_title: 'Host Institutions', requiredPermission: [PagePermssions.VIEW_HOST_INSTITUTION_DASHBOARD , PagePermssions.VIEW_HOST_INSTITUTION_WORKPLACEMENT_DASHBOARD , PagePermssions.VIEW_HOST_INSTITUTION_APPLICANT_DASHBOARD] },
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
        loadChildren: () => import('./modules/host-institution/host-institution.module').then(m => m.HostInstitutionModule),
      },
      {
        path: 'business-competition',
        title: 'Business Competition',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Business Competition', page_title: 'Business Competition', requiredPermission: [PagePermssions.VIEW_BUSINESS_COMPETITION] },
        loadChildren: () => import('./modules/business-competition/business-competition.module').then(m => m.BusinessCompetitionModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'host-institution-applicants',
        title: 'Host Institutions',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Host Institutions', page_title: 'Host Institutions', requiredPermission: [PagePermssions.VIEW_HOST_INSTITUTION_DASHBOARD , PagePermssions.VIEW_HOST_INSTITUTION_WORKPLACEMENT_DASHBOARD , PagePermssions.VIEW_HOST_INSTITUTION_APPLICANT_DASHBOARD] },
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
        loadChildren: () => import('./modules/host-institution-applicants/host-institution-applicants.module').then(m => m.HostInstitutionApplicantsModule),
      },
      {
        path: 'courses/trainings',
        title: 'Trainings',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Trainings', page_title: 'Trainings', requiredPermission: [PagePermssions.VIEW_TRAINING_MANAGEMENT] },
        loadChildren: () => import('./modules/trainings/trainings.module').then(m => m.TrainingsModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'cupf',
        title: 'CUPF',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'CUPF', page_title: 'CUPF', requiredPermission: [PagePermssions.VIEW_CUPF] },
        loadChildren: () => import('./modules/cupf-management/cupf-management.module').then(m => m.CupfManagementModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'pfi',
        title: 'PFI',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'PFI', page_title: 'PFI', requiredPermission: [PagePermssions.VIEW_PFI] },
        loadChildren: () => import('./modules/pfi-management/pfi-management.module').then(m => m.PfiManagementModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'infrastructure-grant',
        title: 'Infrastructure Grant',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Infrastructure Grant', page_title: 'Infrastructure Grant', requiredPermission: [] },
        canActivate: [AuthGuard],
        canLoad: [AuthGuard],
        loadChildren: () => import('./modules/infrastructure-grant/infrastructure-grant.module').then(m => m.InfrastructureGrantModule),
      },
      {
        path: 'infrastructure-grants/:id/:editId',
        title: 'Infrastructure Grands Registration',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Infrastructure Grands Registration', page_title: '', requiredPermission: [] },
        loadChildren: () => import('./modules/apply-grant-infrastructure/apply-grant-infrastructure.module').then(m => m.ApplyGrantInfrastructureModule),
      },      
      {
        path: 'infrastructure-grants/:id',
        title: 'Infrastructure Grands Registration',
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Infrastructure Grands Registration', page_title: '', requiredPermission: [] },
        loadChildren: () => import('./modules/apply-grant-infrastructure/apply-grant-infrastructure.module').then(m => m.ApplyGrantInfrastructureModule),
      },
      {
        path: 'grievance',
        title: 'GRIEVANCE',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'GRIEVANCE', page_title: 'GRIEVANCE', requiredPermission: [PagePermssions.VIEW_GRIEVANCE] },
        loadChildren: () => import('./modules/grievance/grievance.module').then(m => m.GrievanceModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'M&E',
        title: 'M&E',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'M&E', page_title: 'M&E', requiredPermission: [PagePermssions.VIEW_PDOS,PagePermssions.VIEW_FILES,PagePermssions.VIEW_FOLDER, PagePermssions.VIEW_DISTRICT_QUARTERLY_REPORT] },
        loadChildren: () => import('./modules/me/me.module').then(m => m.MeModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'dashboard',
        title: 'DASHBOARD',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Dashboard', page_title: 'Dashboard', requiredPermission: [PagePermssions.VIEW_WEP_DASHBOARD, PagePermssions.VIEW_GRM_DASHBOARD] },
        loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule),
        canLoad: [AuthGuard]
      },   
      {
        path: 'social-norms',
        title: 'Social Norms',
        canActivate: [AuthGuard],
        data: { id: USERTYPE.ADMIN_ID, breadcrumb: 'Social Norms', page_title: 'Social Norms', requiredPermission: [PagePermssions.VIEW_GBV_SESSION] },
        loadChildren: () => import('./modules/social-norms/social-norms.module').then(m => m.SocialNormsModule),
        canLoad: [AuthGuard]
      },   
    ]
  },
  {
    path: 'under-developement',
    title: 'Statisics',
    data: { breadcrumb: 'Under Developement', page_title: 'Under Developement', requiredPermission: [PagePermssions.DAILYCASE_MENU_PERMISSION] },
    component: UnderDevelopmentComponent
  },
  {
    path: 'errors',
    loadChildren: () => import('./core/error-pages/error-pages.module').then(m => m.ErrorPagesModule)
  },
  // Catch-all for unknown URLs — both public website paths and portal
  // paths a user might mistype. Sends them to the access-denied screen,
  // which has a "Back to previous" button that returns them to their
  // last valid page (or the appropriate default landing if they landed
  // here from a fresh tab). See AccessDeniedComponent.goBack() and
  // RouterService.getPreviousNonErrorUrl().
  {
    path: '**',
    redirectTo: routePath?.VIEW_ACCESS_DENIED_PATH,
    pathMatch: 'full'
  },

];
@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }

